import { Button } from "@ravalmatic/catafractum-utils";
import { AudioButtonsContainer } from "./AudioButtonsContainer";
import { SettingsLayer } from "./SettingsLayer";
import { GameManager } from "../../GameManager";
import { GameVars } from "../../GameVars";
import { PersistentDataManager } from "../../PersistentDataManager";
import { Difficulty } from "../../Types";
import { AudioManager } from "../../AudioManager";

export class SplashScene extends Phaser.Scene {

    private static readonly IPAD_PORTRAIT_ASPECT_RATIO = 4 / 3;
    private static readonly WIDE_MOBILE_SCALE = 1.25;
    private static readonly DIFFICULTY_FRAMES: Record<Difficulty, string> = {
        [Difficulty.EASY]: "btn_difficulty_easy",
        [Difficulty.MEDIUM]: "btn_difficulty_medium",
        [Difficulty.HARD]: "btn_difficulty_expert"
    };

    private scaledItemsContainer: Phaser.GameObjects.Container;
    private buttonsContainer: Phaser.GameObjects.Container;
    private settingsLayer: SettingsLayer;
    private settingsDismissArea: Phaser.GameObjects.Zone;
    private gameLogo: Phaser.GameObjects.Image;
    private audioButtonsContainer: AudioButtonsContainer;
    private difficultyButtons: { difficulty: Difficulty; button: Button }[];
    private btnPlay: Button;
    private btnSettings: Button;

    constructor() {

        super("SplashScene");
    }

    public create(): void {

        GameManager.setCurrentScene(this);

        const img = new Phaser.GameObjects.Image(this, GameVars.gameWidth / 2, GameVars.gameHeight / 2, "splash_background");
        img.scaleY = GameVars.scaleY;
        this.add.existing(img);

        this.audioButtonsContainer = new AudioButtonsContainer(this);
        this.add.existing(this.audioButtonsContainer);

        this.btnSettings = new Button(
            this, GameVars.gameWidth - 60, GameVars.gameHeight - 60 * GameVars.scaleY,
            "texture_atlas_1", "btn_settings"
        );
        this.btnSettings.scaleY = GameVars.scaleY;
        this.btnSettings.setInflationTween(1.05, 1, GameVars.scaleY);
        this.btnSettings.onDown(this.onClickSettingsButton, this);
        this.add.existing(this.btnSettings);

        this.settingsDismissArea = new Phaser.GameObjects.Zone(
            this, 0, 0, GameVars.gameWidth, GameVars.gameHeight
        ).setOrigin(0).setVisible(false);
        this.settingsDismissArea.on("pointerdown", () => this.onClickOutsideSettings());
        this.add.existing(this.settingsDismissArea);

        this.scaledItemsContainer = new Phaser.GameObjects.Container(
            this,
            GameVars.gameWidth / 2,
            GameVars.gameHeight / 2
        );
        this.scaledItemsContainer.scaleY = GameVars.scaleY;
        this.add.existing(this.scaledItemsContainer);

        this.gameLogo = new Phaser.GameObjects.Image(this, 0, -200, "texture_atlas_1", "game_logo");
        this.scaledItemsContainer.add(this.gameLogo);

        this.buttonsContainer = new Phaser.GameObjects.Container(this);
        this.scaledItemsContainer.add(this.buttonsContainer);

        this.difficultyButtons = [Difficulty.EASY, Difficulty.MEDIUM, Difficulty.HARD].map((difficulty, index) => {
            const selected = GameVars.gameData.difficulty === difficulty;
            const button = new Button(this, (index - 1) * 205, 40, "texture_atlas_1",
                `${SplashScene.DIFFICULTY_FRAMES[difficulty]}_${selected ? "on" : "off"}`);
            button.setInflationTween(1.025);
            button.onDown(() => this.onClickDifficultyButton(difficulty), this);
            this.buttonsContainer.add(button);
            return { difficulty, button };
        });

        this.btnPlay = new Button(this, 0, 220, "texture_atlas_1", "btn_play");
        this.btnPlay.setInflationTween(1.05);
        this.btnPlay.onDown(this.onClickPlayButton, this);
        this.buttonsContainer.add(this.btnPlay);

        this.settingsLayer = new SettingsLayer(this);
        this.settingsLayer.setVisible(false);
        this.scaledItemsContainer.add(this.settingsLayer);

        this.relayout();
    }

    public relayout(): void {

        if (!this.scaledItemsContainer || typeof window === "undefined") {
            return;
        }

        this.btnSettings.setPosition(GameVars.gameWidth - 60, GameVars.gameHeight - 60 * GameVars.scaleY);
        this.settingsDismissArea.setSize(GameVars.gameWidth, GameVars.gameHeight);

        const isMobile = !this.sys.game.device.os.desktop;
        const aspectRatio = window.innerHeight / window.innerWidth;
        const isWideMobile = isMobile
            && aspectRatio > SplashScene.IPAD_PORTRAIT_ASPECT_RATIO;
        const scale = isWideMobile ? SplashScene.WIDE_MOBILE_SCALE : 1;

        this.scaledItemsContainer.setPosition(GameVars.gameWidth / 2, GameVars.isLandscape ? GameVars.gameHeight / 2 : GameVars.gameHeight / 2  - 80);
        this.scaledItemsContainer.setScale(scale, scale * GameVars.scaleY);
        this.settingsLayer.setGameScaleY(GameVars.scaleY);
    }

    private onClickDifficultyButton(difficulty: Difficulty): void {

        if (!this.buttonsContainer.visible) {
            return;
        }
        if (GameVars.gameData.difficulty === difficulty) {
            return;
        }

        PersistentDataManager.updateGameData("difficulty", difficulty);
        this.refreshDifficultyButtons();
        AudioManager.playSound("click", false);
    }

    private refreshDifficultyButtons(): void {

        this.difficultyButtons.forEach(({ difficulty, button }) => {
            const selected = GameVars.gameData.difficulty === difficulty;
            button.setFrame(`${SplashScene.DIFFICULTY_FRAMES[difficulty]}_${selected ? "on" : "off"}`);
        });
    }

    private onClickPlayButton(): void {

        if (!this.buttonsContainer.visible) {
            return;
        }
        GameManager.enterBoardScene();
    }

    private onClickSettingsButton(): void {

        if (this.settingsLayer.visible || !this.btnSettings.input?.enabled) {
            return;
        }
        this.btnSettings.setVisible(false).disableInteractive();
        this.buttonsContainer.setVisible(false);
        this.difficultyButtons.forEach(({ button }) => button.disableInteractive());
        this.btnPlay.disableInteractive();
        this.settingsLayer.refresh();
        this.settingsLayer.setVisible(true);
        this.settingsDismissArea.setVisible(true).setInteractive();
    }

    private onClickOutsideSettings(): void {

        if (!this.settingsLayer.visible || !this.settingsDismissArea.input?.enabled) {
            return;
        }
        this.settingsLayer.setVisible(false);
        this.settingsDismissArea.setVisible(false).disableInteractive();
        this.buttonsContainer.setVisible(true);
        this.difficultyButtons.forEach(({ button }) => button.setInteractive());
        this.btnPlay.setInteractive();
        this.btnSettings.setVisible(true).setInteractive();
    }
}
